import { GOALS } from '@/lib/goals';
import { AdminMealStructure, structureFields } from '@/components/MealPreferences';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Copy, Utensils, X, ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import { TemplateVersionHistory } from '@/components/TemplateVersionHistory';
import { useResolvedMeals } from './NutritionProgramReview';

const NO_CLIENT = '__none__';
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOTS = ['Breakfast', 'Lunch', 'Protein Snack', 'Snack', 'Dinner', 'Meal 1', 'Meal 2', 'Meal 3', 'Meal 4'];
const GREEN = '#65A30D';

const newDay = (i) => ({ day_index: i, meals: [] });
const newWeek = (num) => ({ week_number: num, days: Array.from({ length: 7 }, (_, i) => newDay(i)) });
const normalizeWeeks = (weeks) => (weeks || []).map((w, wi) => {
  const byIdx = {};
  (w.days || []).forEach(d => { byIdx[d.day_index] = d; });
  return { week_number: wi + 1, days: Array.from({ length: 7 }, (_, i) => byIdx[i] ? { ...byIdx[i], day_index: i, meals: byIdx[i].meals || [] } : newDay(i)) };
});

export default function NutritionPrograms() {
  const [programs, setPrograms] = useState([]);
  const [meals, setMeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState({ goal: '', weeks: 4, structure: '3-if', calories: 2000, protein: 150, notes: '', client_id: NO_CLIENT });
  const [clients, setClients] = useState([]);
  const resolved = useResolvedMeals(open ? form : null);

  const load = async () => {
    const [p, m] = await Promise.all([api.get('/nutrition-programs'), api.get('/meals')]);
    setPrograms(p.data); setMeals(m.data);
  };
  useEffect(() => { load(); api.get('/clients').then(r => setClients(r.data || [])).catch(() => {}); }, []);

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const withClient = ai.client_id !== NO_CLIENT;
      const r = await api.post('/nutrition-programs/ai-generate', { ...ai, ...structureFields(ai.structure), client_id: withClient ? ai.client_id : null,
        calories: withClient ? 0 : ai.calories, protein: withClient ? 0 : ai.protein });
      const v = r.data.validation || {};
      toast[v.passed ? 'success' : 'warning'](v.passed ? `Plan generated — all ${v.days_checked} days within ±${v.tolerance_kcal} kcal`
        : `Plan generated — ${v.failed_days} day(s) outside ±${v.tolerance_kcal} kcal are flagged`);
      await load();
      setAiOpen(false);
      openBuilder(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const openBuilder = (prog) => {
    if (prog) { setEditing(prog.id); setForm({ ...prog, weeks: normalizeWeeks(prog.weeks.length ? prog.weeks : [newWeek(1)]) }); }
    else { setEditing(null); setForm({ name: '', goal: '', description: '', duration_weeks: 4, weeks: [newWeek(1)], is_template: true }); }
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Program name required');
    const payload = { ...form, duration_weeks: form.weeks.length };
    try {
      if (editing) await api.put(`/nutrition-programs/${editing}`, payload);
      else await api.post('/nutrition-programs', payload);
      toast.success('Program saved'); setOpen(false); load();
    } catch { toast.error('Failed to save'); }
  };
  const duplicate = async (id) => { await api.post(`/nutrition-programs/${id}/duplicate`); toast.success('Duplicated'); load(); };
  const del = async (id) => { if (!confirm('Delete this program?')) return; await api.delete(`/nutrition-programs/${id}`); toast.success('Deleted'); load(); };

  const setWeeks = (weeks) => setForm(f => ({ ...f, weeks }));
  const addWeek = () => setWeeks([...form.weeks, newWeek(form.weeks.length + 1)]);
  const dupWeek = (wi) => { const copy = JSON.parse(JSON.stringify(form.weeks[wi])); const weeks = [...form.weeks]; weeks.splice(wi + 1, 0, copy); setWeeks(weeks.map((w, i) => ({ ...w, week_number: i + 1 }))); };
  const delWeek = (wi) => { if (form.weeks.length === 1) return toast.error('Need at least one week'); setWeeks(form.weeks.filter((_, i) => i !== wi).map((w, i) => ({ ...w, week_number: i + 1 }))); };
  const dupDayToNext = (wi, di) => { if (di >= 6) return toast.error('No next day'); const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di + 1].meals = JSON.parse(JSON.stringify(weeks[wi].days[di].meals)); setWeeks(weeks); };
  const clearDay = (wi, di) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].meals = []; setWeeks(weeks); };
  const addMeal = (wi, di, meal_id) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); const used = weeks[wi].days[di].meals.length; weeks[wi].days[di].meals.push({ slot: SLOTS[Math.min(used, 3)], meal_id }); setWeeks(weeks); };
  const updMeal = (wi, di, mi, k, v) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].meals[mi][k] = v; setWeeks(weeks); };
  const delMeal = (wi, di, mi) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].meals.splice(mi, 1); setWeeks(weeks); };

  return (
    <div className="space-y-4" data-testid="nutrition-programs">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground max-w-xl">Build multi-week meal plans from your meals, then assign them to clients. Daily macro targets auto-fill from the plan.</p>
        <div className="flex gap-2">
          <Button onClick={() => setAiOpen(true)} data-testid="ai-generate-nutrition-btn" variant="outline" className="font-semibold border-[var(--c-green-soft)] text-[var(--c-green-ink)]"><Sparkles className="w-4 h-4 mr-2" /> Generate with AI</Button>
          <Button onClick={() => openBuilder(null)} data-testid="add-nutrition-program-btn" className="font-semibold" style={{ background: GREEN, color: '#081726' }}><Plus className="w-4 h-4 mr-2" /> New Program</Button>
        </div>
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--c-green-ink)]" /> AI Meal Plan Generator</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Selects approved meals from your Meal Library, adjusts portions within limits and calculates every macro from the Food Library. You review & edit before assigning.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Goal</Label>
                <Select value={ai.goal} onValueChange={v => setAi({ ...ai, goal: v })}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="ai-nutrition-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
                  <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Client (optional)</Label>
                <Select value={ai.client_id} onValueChange={v => setAi({ ...ai, client_id: v })}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="ai-nutrition-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CLIENT}>No client — enter targets manually</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {ai.client_id === NO_CLIENT ? <>
                <div><Label>Daily kcal</Label><Input type="number" value={ai.calories} onChange={e => setAi({ ...ai, calories: parseInt(e.target.value) || 0 })} data-testid="ai-nutrition-cal" className="bg-transparent border-[var(--c-hair)]" /></div>
                <div><Label>Protein (g)</Label><Input type="number" value={ai.protein} onChange={e => setAi({ ...ai, protein: parseInt(e.target.value) || 0 })} data-testid="ai-nutrition-protein" className="bg-transparent border-[var(--c-hair)]" /></div>
              </> : <div className="col-span-2 text-xs text-muted-foreground" data-testid="ai-nutrition-profile-note">Calorie and protein targets are calculated from this client's profile and the selected goal.</div>}
              <div><Label>Weeks</Label><Input type="number" min="1" max="16" value={ai.weeks} onChange={e => setAi({ ...ai, weeks: parseInt(e.target.value) || 4 })} data-testid="ai-nutrition-weeks" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Meals / day</Label><AdminMealStructure value={ai.structure} onChange={v => setAi({ ...ai, structure: v })} fromClient={ai.client_id !== NO_CLIENT} testid="ai-nutrition-meal-structure" /></div>
            </div>
            <div><Label>Notes (optional)</Label><Textarea value={ai.notes} onChange={e => setAi({ ...ai, notes: e.target.value })} rows={2} placeholder="allergies, dietary style…" className="bg-transparent border-[var(--c-hair)]" /></div>
            <Button onClick={generateAI} disabled={aiLoading} data-testid="ai-generate-nutrition-submit" className="w-full font-semibold" style={{ background: GREEN, color: '#081726' }}>
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating… (~20s)</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Plan</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.length === 0 && <div className="text-sm text-muted-foreground col-span-full py-10 text-center">No nutrition programs yet.</div>}
        {programs.map(p => {
          const totalMeals = (p.weeks || []).reduce((s, w) => s + (w.days || []).reduce((ds, d) => ds + (d.meals || []).length, 0), 0);
          return (
            <div key={p.id} className="regen-card-elevated p-5" data-testid={`nutrition-program-${p.id}`}>
              <div className="flex items-start justify-between">
                <span className="pill text-[10px]" style={{ background: `${GREEN}1a`, color: GREEN }}>{p.goal || 'Plan'}</span>
                <span className="text-[11px] font-mono text-muted-foreground">{p.weeks?.length || 0} wks · {totalMeals} meals</span>
              </div>
              <div className="font-display font-bold text-lg mt-2">{p.name}</div>
              {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
              <div className="flex gap-2 mt-4 items-center">
                <Button size="sm" variant="outline" onClick={() => openBuilder(p)} data-testid={`edit-nutrition-program-${p.id}`} className="border-[var(--c-hair)]"><Edit2 className="w-3 h-3 mr-1.5" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(p.id)} className="border-[var(--c-hair)]"><Copy className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(p.id)} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                <div className="ml-auto"><TemplateVersionHistory ttype="nutrition-programs" tid={p.id} name={p.name} onRestored={load} /></div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Nutrition Program</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Program Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="nprog-name" placeholder="e.g. Fat Loss Meal Plan" className="bg-transparent border-[var(--c-hair)]" /></div>
                <div>
                  <Label>Goal</Label>
                  <Select value={form.goal} onValueChange={v => setForm({ ...form, goal: v })}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="nprog-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
                    <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-transparent border-[var(--c-hair)]" rows={2} /></div>
              <div className="space-y-3">
                {form.weeks.map((wk, wi) => (
                  <div key={wi} className="rounded-xl border border-[var(--c-hair)] overflow-hidden" data-testid={`nprog-week-${wi}`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--c-film)]">
                      <div className="font-display font-bold text-sm">Week {wk.week_number}</div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => dupWeek(wi)} className="h-7 border-[var(--c-hair)] text-xs"><Copy className="w-3 h-3 mr-1" /> Duplicate</Button>
                        <Button size="sm" variant="outline" onClick={() => delWeek(wi)} className="h-7 border-red-500/30 text-red-400 text-xs"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="divide-y divide-[var(--c-hair)]">
                      {wk.days.map((day, di) => (
                        <NutriDayRow key={di} day={day} wi={wi} di={di} meals={meals} resolved={resolved}
                          addMeal={addMeal} updMeal={updMeal} delMeal={delMeal} dupDayToNext={dupDayToNext} clearDay={clearDay} />
                      ))}
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addWeek} data-testid="nadd-week-btn" className="w-full border-dashed border-[var(--c-hair)] text-muted-foreground"><Plus className="w-4 h-4 mr-2" /> Add Week</Button>
              </div>
              <Button onClick={save} data-testid="save-nutrition-program-btn" className="w-full font-semibold" style={{ background: GREEN, color: '#081726' }}>Save Program</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NutriDayRow({ day, wi, di, meals, resolved, addMeal, updMeal, delMeal, dupDayToNext, clearDay }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const mname = (id) => resolved[id]?.name || meals.find(m => m.id === id)?.name || 'Meal';
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={`nprog-day-${wi}-${di}`}>
      <div className="w-10 pt-1 text-xs font-bold text-muted-foreground uppercase">{DAYS[di]}</div>
      <div className="flex-1 min-w-0">
        {day.target_kcal ? (
          <div className={`text-[11px] font-mono mb-1.5 ${day.within_tolerance ? 'text-[var(--c-green-ink)]' : 'text-red-400'}`} data-testid={`nprog-day-check-${wi}-${di}`}>
            {day.totals?.calories} / {day.target_kcal} kcal ({day.deviation_kcal > 0 ? '+' : ''}{day.deviation_kcal}) · P {day.totals?.protein_g}/{day.target_protein} g
            {!day.within_tolerance && day.flag_reason ? ` — ${day.flag_reason}` : ''}
          </div>
        ) : null}
        <div className="space-y-1.5">
          {day.meals.length === 0 && <span className="text-xs text-muted-foreground/60">No meals planned</span>}
          {day.meals.map((mealItem, mi) => (
            <div key={mi} className="flex items-center gap-2">
              <Select value={mealItem.slot} onValueChange={v => updMeal(wi, di, mi, 'slot', v)}>
                <SelectTrigger className="h-7 w-28 bg-transparent border-[var(--c-hair)] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{(SLOTS.includes(mealItem.slot) ? SLOTS : [mealItem.slot, ...SLOTS]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <span className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs flex-1 min-w-0" style={{ background: `${GREEN}1a`, color: GREEN }}>
                <Utensils className="w-3 h-3 shrink-0" /><span className="truncate">{mname(mealItem.meal_id)}</span>
                <button onClick={() => delMeal(wi, di, mi)} className="ml-auto opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <div className="relative">
            <button onClick={() => setPickerOpen(o => !o)} data-testid={`add-meal-${wi}-${di}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)]"><Plus className="w-3 h-3" /> Meal <ChevronDown className="w-3 h-3" /></button>
            {pickerOpen && (
              <div className={`absolute z-50 ${di >= 5 ? 'bottom-full mb-1' : 'mt-1'} w-56 max-h-52 overflow-y-auto rounded-lg border border-[var(--c-hair)] bg-[var(--c-elev)] shadow-xl p-1`}>
                {meals.length === 0 && <div className="text-[11px] text-muted-foreground p-2">No meals. Create some in Meal Library.</div>}
                {meals.map(m => <button key={m.id} onClick={() => { addMeal(wi, di, m.id); setPickerOpen(false); }} className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-[var(--c-film)]">{m.name} <span className="text-muted-foreground">· {m.calories}kcal</span></button>)}
              </div>
            )}
          </div>
          {day.meals.length > 0 && <>
            <button onClick={() => dupDayToNext(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground"><Copy className="w-3 h-3 inline mr-1" />Copy → next day</button>
            <button onClick={() => clearDay(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground">Clear</button>
          </>}
        </div>
      </div>
    </div>
  );
}
